"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createAimer = createAimer;
const solve_1 = require("./solve");
const trainer_1 = require("./trainer");
const types_1 = require("./types");
/** The head pans at most this far either side of the body's facing. */
const MAX_PAN = 90;
const MIN_RANGE = 0.1; // m, launch point to target
/** Above this a faster shot to the same point would fly higher, not flatter. */
const MAX_FLAT_PITCH = 45;
const PITCH_RANGE = [-60, 80];
const MAX_ITERATIONS = 25;
const toDeg = (rad) => (rad * 180) / Math.PI;
const toRad = (deg) => (deg * Math.PI) / 180;
/** Wrap an angle in degrees to (-180, 180]. */
function wrapDeg(angle) {
    const w = ((angle % 360) + 360) % 360;
    return w > 180 ? w - 360 : w;
}
/** Compass bearing from one point to another: 0 = +y, positive = clockwise. */
const bearing = (from, to) => toDeg(Math.atan2(to.x - from.x, to.y - from.y));
/** Height the plan aims at: > 0 is an aerial target, 0 a landing. */
const targetHeight = (plan) => (Number.isFinite(plan.target.z) && plan.target.z > 0 ? plan.target.z : 0);
const fail = (reason, message, closest) => ({ ok: false, reason, message, closest });
function createAimer(profile, kernel, options) {
    const { gravity } = profile.environment;
    const { limits } = profile.trainer.mechanism;
    // Finite-difference steps: well above the integrator's noise, well below the tolerance
    const fdScale = kernel.integrator === "legacy" ? 100 : 1;
    const fdStep = { pitch: 1e-3 * fdScale, speed: 1e-4 * fdScale, yaw: 1e-3 * fdScale };
    const end = new Float64Array(8);
    /** Validity checks that need no flight, in v1's order. */
    function checkPlan(plan, forward, pan) {
        const range = Math.hypot(plan.target.x - forward.x, plan.target.y - forward.y);
        if (Math.abs(pan) >= MAX_PAN)
            return fail("targetBehind", "target must be in front of the trainer");
        if (range < MIN_RANGE)
            return fail("targetTooClose", "launch and target point must be different");
        if (plan.shape === "peakHeight") {
            if (plan.peakHeight < forward.z)
                return fail("peakBelowLaunch", "peak height must be >= launch height");
            if (plan.peakHeight < targetHeight(plan)) {
                return fail("peakBelowTarget", "peak height must be >= target height");
            }
        }
        if (plan.shape === "speed" && !(plan.speed > 0 && plan.speed <= limits.maxSpeed)) {
            return fail("speedOutOfRange", `speed must be in (0, ${limits.maxSpeed}] m/s`);
        }
        return null;
    }
    /** No-drag, no-spin starting point for each plan shape. */
    function unknownsFor(plan, forward, pan) {
        const spin = plan.spin ?? types_1.NO_SPIN;
        const range = Math.hypot(plan.target.x - forward.x, plan.target.y - forward.y);
        const rise = targetHeight(plan) - forward.z;
        const speedBounds = [limits.minSpeed, limits.maxSpeed];
        const clampSpeed = (v) => Math.min(limits.maxSpeed, Math.max(limits.minSpeed, v));
        switch (plan.shape) {
            case "peakHeight": {
                // up hDiff to the peak, down `fall` to the target height
                const hDiff = plan.peakHeight - forward.z;
                const fall = plan.peakHeight - targetHeight(plan);
                const c = hDiff + Math.sqrt(fall * hDiff);
                const pitch = toDeg(Math.atan((2 * c) / range));
                const speed = Math.sqrt(2 * gravity * hDiff * (range * range + 4 * c * c)) / (2 * c);
                if (options.peakHeight === "v1") {
                    return {
                        names: ["speed", "yaw"],
                        start: [clampSpeed(speed), pan],
                        lower: [speedBounds[0], -MAX_PAN],
                        upper: [speedBounds[1], MAX_PAN],
                        launch: (u) => ({ pitch, speed: u[0], yaw: u[1], spin }),
                    };
                }
                return {
                    names: ["pitch", "speed", "yaw"],
                    start: [pitch, clampSpeed(speed), pan],
                    lower: [PITCH_RANGE[0], speedBounds[0], -MAX_PAN],
                    upper: [PITCH_RANGE[1], speedBounds[1], MAX_PAN],
                    launch: (u) => ({ pitch: u[0], speed: u[1], yaw: u[2], spin }),
                };
            }
            case "pitch": {
                const cos = Math.cos(toRad(plan.pitch));
                const height = 2 * cos * cos * (range * Math.tan(toRad(plan.pitch)) - rise);
                const speed = height > 0 ? Math.sqrt((gravity * range * range) / height) : limits.maxSpeed;
                return {
                    names: ["speed", "yaw"],
                    start: [clampSpeed(speed), pan],
                    lower: [speedBounds[0], -MAX_PAN],
                    upper: [speedBounds[1], MAX_PAN],
                    launch: (u) => ({ pitch: plan.pitch, speed: u[0], yaw: u[1], spin }),
                };
            }
            case "speed": {
                const v = plan.speed;
                const disc = v ** 4 - gravity * (gravity * range * range + 2 * rise * v * v);
                const pitch = disc >= 0 ? toDeg(Math.atan((v * v - Math.sqrt(disc)) / (gravity * range))) : 35;
                return {
                    names: ["pitch", "yaw"],
                    start: [pitch, pan],
                    lower: [PITCH_RANGE[0], -MAX_PAN],
                    upper: [MAX_FLAT_PITCH, MAX_PAN],
                    launch: (u) => ({ pitch: u[0], speed: v, yaw: u[1], spin }),
                };
            }
        }
    }
    return function aim(pose, plan) {
        const forward = (0, trainer_1.launchPoint)(profile.trainer.geometry, pose);
        const pan = wrapDeg(bearing(forward, plan.target) - pose.yaw);
        const invalid = checkPlan(plan, forward, pan);
        if (invalid)
            return invalid;
        const unknowns = unknownsFor(plan, forward, pan);
        const until = targetHeight(plan) > 0 ? targetHeight(plan) : "landing";
        const apex = plan.shape === "peakHeight" && options.peakHeight === "apex" ? plan.peakHeight : null;
        const residual = (u, r) => {
            // Net and walls are left out of the solve: clipping them makes the
            // landing jump, which stalls Newton. The real flight is checked after.
            if (!kernel.endpoint((0, trainer_1.release)(profile, pose, unknowns.launch(u), kernel.spinModel), until, end, "ground"))
                return false;
            r[0] = end[1] - plan.target.x;
            r[1] = end[2] - plan.target.y;
            if (apex !== null)
                r[2] = end[7] - apex;
            return true;
        };
        const solution = (0, solve_1.solve)(residual, unknowns.start, {
            lower: unknowns.lower,
            upper: unknowns.upper,
            steps: unknowns.names.map((name) => fdStep[name]),
            maxIterations: MAX_ITERATIONS,
            // horizontal miss, and for a held apex its height error too
            done: (r) => Math.hypot(r[0], r[1]) <= options.tolerance
                && (r.length < 3 || Math.abs(r[2]) <= options.tolerance),
        });
        if (!solution.defined) {
            return fail("outOfReach", "the starting throw never reaches the target");
        }
        const found = aimAt(pose, plan, unknowns.launch(solution.u), solution.iterations);
        if (solution.converged) {
            const struck = strikesOnTheWay(found);
            return struck
                ? fail("blocked", `the throw that reaches the target strikes the ${struck} on the way`, found)
                : { ok: true, ...found };
        }
        if (plan.shape === "speed" && solution.u[0] === unknowns.lower[0]) {
            return fail("targetTooClose", "target too close for this speed", found);
        }
        // Stalled short of the target: at a speed limit, or (for a fixed
        // speed) at the pitch that carries furthest. Either way, out of reach.
        if (solution.pinned || fallsShort(found, plan)) {
            return fail("outOfReach", "the target is out of the trainer's reach for this plan", found);
        }
        return fail("noConvergence", `missed by ${found.miss.toFixed(3)} m after ${solution.iterations} iterations`, found);
    };
    /** The first net or wall the solved flight strikes before its end, if any. */
    function strikesOnTheWay(found) {
        const endT = found.flight.end?.t ?? Infinity;
        const struck = found.flight.contacts.find((c) => c.kind !== "ground" && c.t <= endT);
        return struck ? struck.surface : null;
    }
    /** Whether the best attempt came up short of the target, along the line to it. */
    function fallsShort(found, plan) {
        const reached = found.flight.end?.position;
        if (!reached)
            return true;
        const toTarget = Math.hypot(plan.target.x - found.origin.x, plan.target.y - found.origin.y);
        const carried = ((reached.x - found.origin.x) * (plan.target.x - found.origin.x)
            + (reached.y - found.origin.y) * (plan.target.y - found.origin.y)) / toTarget;
        return carried < toTarget - options.tolerance;
    }
    function aimAt(pose, plan, launch, iterations) {
        const released = (0, trainer_1.release)(profile, pose, launch, kernel.spinModel);
        const z = targetHeight(plan);
        const flight = kernel.fly(released, {
            until: z > 0 ? { kind: "descending", z } : { kind: "landing" },
        });
        const reached = flight.end?.position;
        return {
            launch,
            motors: profile.trainer.mechanism.toMotors(launch.speed, launch.spin),
            origin: released.origin,
            deflection: released.deflection,
            flight,
            miss: reached ? Math.hypot(reached.x - plan.target.x, reached.y - plan.target.y) : Infinity,
            iterations,
        };
    }
}
